// Pure theory for the /home-key course: the home note, scale degrees, the
// phrase model, and the "soft checks" that nudge (never grade) a recorded
// phrase. No audio, no React — everything here is unit-tested.
//
// The whole course lives in one key: G major. Notes are talked about as scale
// degrees 1–7 relative to home (G); letter names appear only incidentally.
// Unit 6 tilts the same seven notes around a new home — E, the relative
// minor — so the minor side reuses everything here and renumbers it.

// G3 — the home note, and the bottom of the on-screen piano.
export const HOME_MIDI = 55;

// The on-screen piano range: G3..G5 inclusive (25 keys, home at both ends).
export const PIANO_RANGE = { low: 55, high: 79 };

// Semitone offsets of the major scale from the tonic.
export const MAJOR_SCALE_SEMIS = [0, 2, 4, 5, 7, 9, 11];

// Scale degree (1..7) of a MIDI note relative to home, in any octave.
// Returns null for out-of-scale notes (and anything non-numeric).
export const degreeOf = (midi) => {
  if (!Number.isFinite(midi)) return null;
  const pc = (((midi - HOME_MIDI) % 12) + 12) % 12;
  const i = MAJOR_SCALE_SEMIS.indexOf(pc);
  return i === -1 ? null : i + 1;
};

// Is this note in the G major scale (any octave)?
export const isInScale = (midi) => degreeOf(midi) !== null;

// MIDI note number → frequency in Hz (A4 = 69 = 440 Hz).
export const midiToFreq = (midi) => 440 * 2 ** ((midi - 69) / 12);

// The house-name of each degree — how the course talks about the notes.
export const DEGREE_PERSONA = {
  1: "home",
  2: "step-off",
  3: "warm",
  4: "leaning",
  5: "strong-away",
  6: "wistful",
  7: "the doorbell",
};

// Short display label for a degree, e.g. "1 · home". Empty for unknown.
export const degreeLabel = (degree) =>
  DEGREE_PERSONA[degree] ? `${degree} · ${DEGREE_PERSONA[degree]}` : "";

// Unit 2's interval palette: each distance above home has a flavor, the way
// each degree has a persona. `steps` is the interval's scale-step size
// (2nd..octave); `semis` its size in semitones above home within G major —
// every one lands on a scale tone.
export const INTERVAL_FLAVORS = [
  { steps: 2, label: "2nd", semis: 2, flavor: "the rub" },
  { steps: 3, label: "3rd", semis: 4, flavor: "sweet" },
  { steps: 4, label: "4th", semis: 5, flavor: "open" },
  { steps: 5, label: "5th", semis: 7, flavor: "strong" },
  { steps: 6, label: "6th", semis: 9, flavor: "the warm ache" },
  { steps: 7, label: "7th", semis: 11, flavor: "the reach" },
  { steps: 8, label: "octave", semis: 12, flavor: "the mirror" },
];

/* ============================================================
   Phrase model — { notes: [{ midi, at, dur, vel? }] }
   `at` / `dur` are in ms from phrase start. Recording is free-time
   (no quantization); playback is verbatim.
   ============================================================ */

// Total length of a phrase in ms — when the last ringing note ends.
export const phraseLength = (phrase) => {
  const notes = (phrase && phrase.notes) || [];
  return notes.reduce((end, n) => Math.max(end, (n.at || 0) + (n.dur || 0)), 0);
};

// Number of melodic leaps in a phrase: consecutive notes (in time order, ties
// low-to-high) more than a whole step — 2 semitones — apart. Steps walk;
// anything bigger is a leap.
export const countLeaps = (phrase) => {
  const notes = [...((phrase && phrase.notes) || [])].sort(
    (a, b) => (a.at || 0) - (b.at || 0) || a.midi - b.midi
  );
  let leaps = 0;
  for (let i = 1; i < notes.length; i += 1) {
    if (Math.abs(notes[i].midi - notes[i - 1].midi) > 2) leaps += 1;
  }
  return leaps;
};

// The phrase's final note — the latest by `at`; ties go to the highest pitch
// (the ear hears the top of a simultaneous ending). Null for an empty phrase.
export const lastNote = (phrase) => {
  const notes = (phrase && phrase.notes) || [];
  return notes.reduce((best, n) => {
    if (!best) return n;
    if ((n.at || 0) > (best.at || 0)) return n;
    if ((n.at || 0) === (best.at || 0) && n.midi > best.midi) return n;
    return best;
  }, null);
};

// Compare an echoed phrase to its call, note by note in played order.
// Pitch-class tolerant: the same note in a different octave counts (a MIDI
// keyboard living an octave down shouldn't read as a miss). Timing is
// ignored — pitch first; rhythm is later units' business.
// Returns { matched, total, extra, missing, firstMissIndex, firstMissDelta,
// perfect } where firstMissDelta is got-minus-wanted in semitones (null when
// the note simply wasn't played).
export const compareEcho = (call, echo) => {
  const seq = (phrase) =>
    ((phrase && phrase.notes) || [])
      .slice()
      .sort((a, b) => (a.at || 0) - (b.at || 0))
      .map((n) => n.midi);
  const want = seq(call);
  const got = seq(echo);
  const total = want.length;
  let matched = 0;
  let firstMissIndex = -1;
  let firstMissDelta = 0;
  for (let i = 0; i < total; i += 1) {
    if (got[i] != null && (got[i] - want[i]) % 12 === 0) matched += 1;
    else if (firstMissIndex < 0) {
      firstMissIndex = i;
      firstMissDelta = got[i] == null ? null : got[i] - want[i];
    }
  }
  return {
    matched,
    total,
    extra: Math.max(0, got.length - total),
    missing: Math.max(0, total - got.length),
    firstMissIndex,
    firstMissDelta,
    perfect: total > 0 && matched === total && got.length === total,
  };
};

/* ============================================================
   Chords — Units 4–5. A chord is a degree with a color: three scale
   tones stacked in thirds, voiced once as data so every play of
   "the 4 chord" lands under the same hand.
   ============================================================ */

// Fixed smooth voicings: degree (1..7) → three midis, low to high. Every
// note sits in PIANO_RANGE and in the scale, and the whole set lives inside
// one comfortable octave (G3..G4): 1–3 are root position, 4–6 tuck their
// root on top (second inversion), 7 leans on its first inversion. Moving
// between adjacent degrees never asks the hand to travel far — the test
// holds each step to a small movement budget.
export const TRIADS = {
  1: [55, 59, 62], // G3 B3 D4 — home, root position
  2: [57, 60, 64], // A3 C4 E4
  3: [59, 62, 66], // B3 D4 F#4
  4: [55, 60, 64], // G3 C4 E4 — the 4 chord over home's G
  5: [57, 62, 66], // A3 D4 F#4
  6: [59, 64, 67], // B3 E4 G4 — the sad twin, sharing home's roof
  7: [57, 60, 66], // A3 C4 F#4
};

// The voicing for a degree — a fresh copy, so callers can decorate it
// without redecorating TRIADS. Empty for anything that isn't 1..7.
export const chordMidis = (degree) => (TRIADS[degree] || []).slice();

// The house-name of each chord — how the course talks about the triads.
export const CHORD_PERSONA = {
  1: "home",
  2: "restless",
  3: "bittersweet",
  4: "leaning",
  5: "the pull",
  6: "the sad twin",
  7: "on tiptoe",
};

// Before names come jobs: every chord is either home, away, or tension.
// Progressions are trips — leave home, wander, build tension, come back.
export const CHORD_JOBS = { home: [1], away: [2, 3, 4, 6], tension: [5, 7] };

// Which job a chord degree does; null for anything that isn't 1..7.
export const jobOf = (degree) =>
  Object.keys(CHORD_JOBS).find((job) => CHORD_JOBS[job].includes(degree)) ||
  null;

/* ============================================================
   The minor side — Unit 6. The notes don't change; the gravity does.
   Home moves from G to E (the relative minor), the same seven pitch
   classes renumber themselves around the new home, and one borrowed
   note — D# — rebuilds the doorbell.
   ============================================================ */

// E4 — the minor home. Only the pitch class matters: every E is home on
// the minor side, the way every G is home on the major side.
export const MINOR_HOME_MIDI = 64;

// Semitone offsets of the natural minor scale from its tonic.
const NATURAL_MINOR_SEMIS = [0, 2, 3, 5, 7, 8, 10];

// Scale degree (1..7) of a MIDI note relative to the minor home, in any
// octave: E F# G A B C D → 1..7. These are the same seven pitch classes as
// G major — isInScale still says who's in the neighborhood — but the
// numbers tilt around the new home (the old home G is the minor side's 3).
// Returns null for out-of-scale notes (and anything non-numeric).
export const minorDegreeOf = (midi) => {
  if (!Number.isFinite(midi)) return null;
  const pc = (((midi - MINOR_HOME_MIDI) % 12) + 12) % 12;
  const i = NATURAL_MINOR_SEMIS.indexOf(pc);
  return i === -1 ? null : i + 1;
};

// How the minor side renumbers the chords: minor degree → the major degree
// whose voicing it borrows (minor 1 is major 6's chord, and so on around
// the circle). Exported as data so UIs can show the "same chord, new
// number" trick directly.
export const MINOR_TO_MAJOR_DEGREE = { 1: 6, 2: 7, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5 };

// The minor triads are the major triads renumbered — the very same voicing
// arrays (references, not copies), wearing new numbers. Minor 1 is
// TRIADS[6]: the sad twin, promoted to home.
export const MINOR_TRIADS = Object.fromEntries(
  Object.entries(MINOR_TO_MAJOR_DEGREE).map(([minor, major]) => [
    minor,
    TRIADS[major],
  ])
);

// The borrowed doorbell: the minor 5 chord with its middle note raised
// from D to D# (B3 D#4 F#4). The natural minor 7 (D) doesn't pull toward
// home; borrowing D# rebuilds the doorbell. D# (63) is deliberately
// out-of-scale — the one genuinely new note on the minor side. In a
// progression's degree list it's written as the token '5+'.
export const RAISED_V = [59, 63, 66];

// The house-names of the minor chords — same voicings as the major side,
// different weather. The '5+' entry is the raised V's persona.
export const MINOR_CHORD_PERSONA = {
  1: "home, in shadow",
  2: "uneasy",
  3: "the sunny cousin",
  4: "longing",
  5: "soft pull",
  6: "far from home",
  7: "the old road",
  "5+": "the borrowed doorbell",
};

// Jobs transfer wholesale: renumber the degrees and the same three jobs
// come along for the trip.
export const MINOR_CHORD_JOBS = { home: [1], away: [2, 3, 4, 6], tension: [5, 7] };

// Which job a minor chord degree does — including the '5+' token, which is
// tension with the doorbell reinstalled. Null off the map.
export const minorJobOf = (degree) => {
  if (degree === "5+") return "tension";
  return (
    Object.keys(MINOR_CHORD_JOBS).find((job) =>
      MINOR_CHORD_JOBS[job].includes(degree)
    ) || null
  );
};

// Render a progression of chord degrees as a phrase: each chord's three
// notes are simultaneous (same `at`), and the chords step through time one
// after another — playable by playPhrase like anything else. `opts.mode`
// picks the side: 'major' (the default) indexes TRIADS; 'minor' indexes
// MINOR_TRIADS, where the degree token '5+' renders RAISED_V.
export const chordPhrase = (degrees = [], opts = {}) => {
  const chordMs = opts.chordMs == null ? 1100 : opts.chordMs;
  const gapMs = opts.gapMs == null ? 80 : opts.gapMs;
  const minor = opts.mode === "minor";
  const notes = [];
  degrees.forEach((degree, i) => {
    const at = i * (chordMs + gapMs);
    const midis = !minor
      ? chordMidis(degree)
      : degree === "5+"
      ? RAISED_V.slice()
      : (MINOR_TRIADS[degree] || []).slice();
    midis.forEach((midi) => notes.push({ midi, at, dur: chordMs }));
  });
  return { notes };
};

// A progression as a trip, in words: "home → away → tension → home".
// `mode` picks whose jobs table names the stops; the '5+' token reads as
// tension either way. Unknown degrees read as "?"; an empty progression is
// an empty string.
export const describeProgression = (degrees = [], mode = "major") =>
  degrees
    .map((degree) => {
      if (degree === "5+") return "tension";
      return (mode === "minor" ? minorJobOf(degree) : jobOf(degree)) || "?";
    })
    .join(" → ");

/* ============================================================
   Soft checks — a registry of pure functions keyed by name, so lesson
   data can reference them serializably. Each returns { pass, message }
   where the message is a warm nudge, never a verdict: every check's
   result sits next to an "actually, I like it" override in the UI.
   ============================================================ */

const EMPTY_NUDGE = "Play a few notes first — the piano is all yours.";
const EMPTY_PROG_NUDGE = "Pick a chord or two first — the slots are all yours.";
const EMPTY_BEAT_NUDGE = "Tap a few cells first — the grid is all yours.";
const EMPTY_STAGE_NUDGE = "Toggle a few layers first — the stages are all yours.";
const EMPTY_ARRANGE_NUDGE = "Pick a section or two first — the slots are all yours.";

// Total hits across a drum pattern's lanes ({ kick, snare, hat, hatOpen? }).
const countBeatHits = (pattern) =>
  ["kick", "snare", "hat", "hatOpen"].reduce(
    (sum, lane) => sum + ((pattern && pattern[lane]) || []).length,
    0
  );

// A stage's layer mix as a comparable string — the layers that are ON, sorted.
// Accepts either an object of booleans ({ drums: true, ... }) or an array of
// layer names; both are how LayerBoard stages travel.
const stageSignature = (stage) => {
  const layers = (stage && stage.layers) || {};
  const on = Array.isArray(layers)
    ? layers.slice().sort()
    : Object.keys(layers)
        .filter((key) => layers[key])
        .sort();
  return on.join("+");
};

export const CHECKS = {
  // Does the phrase end on this scale degree (any octave)?
  endsOnDegree: (phrase, degree) => {
    const last = lastNote(phrase);
    if (!last) return { pass: false, message: EMPTY_NUDGE };
    if (degreeOf(last.midi) === degree) {
      return {
        pass: true,
        message:
          degree === 1
            ? "Landed. Feel that? That's home."
            : `Ended right on ${degreeLabel(degree)}.`,
      };
    }
    return {
      pass: false,
      message:
        degree === 1
          ? "That ending floats. Lovely — but this one wants a landing on home (1). Bring it home, or keep it as-is?"
          : `This one wants to end on ${degreeLabel(degree)}. Try one more take — or keep yours if you like the drift.`,
    };
  },

  // Does the phrase end on any of these degrees?
  endsOnAnyDegree: (phrase, degrees = []) => {
    const last = lastNote(phrase);
    if (!last) return { pass: false, message: EMPTY_NUDGE };
    const deg = degreeOf(last.midi);
    if (degrees.includes(deg)) {
      return { pass: true, message: `Ended on ${degreeLabel(deg)} — the door stays open.` };
    }
    return {
      pass: false,
      message: `Try ending on ${degrees.join(", ")} — somewhere that leaves a little unfinished business. Or keep this one if it says what you mean.`,
    };
  },

  // Unit 6: does the phrase end on this minor-side degree (any octave)?
  // The same landing check as endsOnDegree, heard from the shadow home.
  endsOnMinorDegree: (phrase, degree) => {
    const last = lastNote(phrase);
    if (!last) return { pass: false, message: EMPTY_NUDGE };
    if (minorDegreeOf(last.midi) === degree) {
      return {
        pass: true,
        message:
          degree === 1
            ? "Landed — same floorboards, dimmer light. That's the shadow home."
            : `Ended right on the minor side's ${degree}.`,
      };
    }
    return {
      pass: false,
      message:
        degree === 1
          ? "That ending hangs in the dusk. Lovely — but this one wants to settle on the shadow home (the minor 1, E). Bring it home, or keep the hover?"
          : `This one wants to end on the minor side's ${degree}. One more take — or keep yours if it leans the way you like.`,
    };
  },

  // At least n notes.
  minNotes: (phrase, n) => {
    const count = ((phrase && phrase.notes) || []).length;
    if (count >= n) return { pass: true, message: "Plenty to work with." };
    if (count === 0) return { pass: false, message: EMPTY_NUDGE };
    return {
      pass: false,
      message: `A couple more notes would give this one room to breathe — aim for at least ${n}.`,
    };
  },

  // At most n notes.
  maxNotes: (phrase, n) => {
    const count = ((phrase && phrase.notes) || []).length;
    if (count <= n) return { pass: true, message: "Nice and compact." };
    return {
      pass: false,
      message: `That's a whole saga! Little phrases loop best — try trimming to ${n} notes or fewer. (Or keep the saga. Sagas are allowed.)`,
    };
  },

  // At most n melodic leaps (see countLeaps) — melodies spend leaps like money.
  maxLeaps: (phrase, n) => {
    const notes = (phrase && phrase.notes) || [];
    if (!notes.length) return { pass: false, message: EMPTY_NUDGE };
    const leaps = countLeaps(phrase);
    if (leaps <= n) {
      return {
        pass: true,
        message:
          leaps === 0
            ? "All steps — smooth as speech."
            : `Mostly steps, with ${leaps} leap${leaps === 1 ? "" : "s"} well spent.`,
      };
    }
    return {
      pass: false,
      message:
        `That's ${leaps} leaps — when every move is big, none of them feel big. ` +
        `Try spending at most ${n}, or keep the wild ride if it thrills you.`,
    };
  },

  // The phrase's highest note happens exactly once — one clear summit.
  singlePeak: (phrase) => {
    const notes = (phrase && phrase.notes) || [];
    if (!notes.length) return { pass: false, message: EMPTY_NUDGE };
    const top = Math.max(...notes.map((note) => note.midi));
    const count = notes.filter((note) => note.midi === top).length;
    if (count === 1) {
      return { pass: true, message: "One clear summit — the line knows where it's going." };
    }
    return {
      pass: false,
      message:
        `Your highest note shows up ${count} times, so the line has a plateau ` +
        `instead of a peak. One single summit usually reads clearer — reshape ` +
        `it, or keep the plateau if you love the view.`,
    };
  },

  // Whole phrase fits inside this many ms.
  withinMs: (phrase, ms) => {
    const len = phraseLength(phrase);
    if (len <= ms) return { pass: true, message: "Fits just right." };
    return {
      pass: false,
      message: `Lovely wander — this one wants to fit inside about ${Math.round(ms / 1000)} seconds, though. A shorter take will loop better.`,
    };
  },

  /* --- Progression checks (Units 4–5). These read `.degrees` off the
     first argument, so runChecks stays generic: build moments pass
     { degrees } where try moments pass { notes }. --- */

  // Does the trip actually go somewhere — a stop that isn't home? Any
  // non-1 counts, wherever it sits (a loop is allowed to start away), and
  // the minor side's '5+' token is about as non-1 as a chord gets.
  progLeavesHome: (prog) => {
    const degrees = (prog && prog.degrees) || [];
    if (!degrees.length) return { pass: false, message: EMPTY_PROG_NUDGE };
    if (degrees.some((degree) => degree !== 1)) {
      return { pass: true, message: "Off you go — this trip actually leaves the house." };
    }
    return {
      pass: false,
      message:
        "Home, home, home — the coziest trip that never happens. Swap a slot " +
        "for somewhere away, or keep the night in if that's the mood.",
    };
  },

  // Does the trip come back — landing on 1, or (since progressions loop)
  // swinging back through a 1 somewhere along the way? Only a literal 1 is
  // home; '5+' and friends all count as being out of the house.
  progComesHome: (prog) => {
    const degrees = (prog && prog.degrees) || [];
    if (!degrees.length) return { pass: false, message: EMPTY_PROG_NUDGE };
    if (degrees[degrees.length - 1] === 1) {
      return { pass: true, message: "Landed on home — a journey with a period at the end." };
    }
    if (degrees.includes(1)) {
      return {
        pass: true,
        message: "No hard landing — but the loop swings back through home every time it comes around.",
      };
    }
    return {
      pass: false,
      message:
        "This trip never touches home — moody! Without a 1 the wandering " +
        "can't land. Give one slot to home, or keep floating if floating is the point.",
    };
  },

  // Is there a 5 or a 7 in the trip — something tugging back toward home?
  // (The tension degrees are 5 and 7 on both sides of the key, and the
  // minor side's borrowed doorbell '5+' is tension by definition.)
  progHasTension: (prog) => {
    const degrees = (prog && prog.degrees) || [];
    if (!degrees.length) return { pass: false, message: EMPTY_PROG_NUDGE };
    if (
      degrees.some(
        (degree) => degree === "5+" || CHORD_JOBS.tension.includes(degree)
      )
    ) {
      return { pass: true, message: "There's the pull — a little tension to make coming home feel earned." };
    }
    return {
      pass: false,
      message:
        "Every stop here is easy company — nothing tugging you home. A 5 or " +
        "a 7 gives the return its reason. Try one, or keep the calm if calm is the point.",
    };
  },

  // Between min and max chords — enough trip to go somewhere, short enough to loop.
  progLen: (prog, min = 1, max = Infinity) => {
    const degrees = (prog && prog.degrees) || [];
    if (!degrees.length) return { pass: false, message: EMPTY_PROG_NUDGE };
    if (degrees.length < min) {
      return {
        pass: false,
        message: `A trip wants at least ${min} stops — add a chord or two so it has somewhere to go.`,
      };
    }
    if (degrees.length > max) {
      return {
        pass: false,
        message: `That's an odyssey! ${max} chords or fewer will loop tighter — or keep the epic. Epics are allowed.`,
      };
    }
    return { pass: true, message: "A good length for a trip." };
  },

  /* --- Groove & arrangement checks (Units 7–8). Same trick as the
     progression checks: runChecks stays generic, and these read the shape
     their moment saves — beat checks read `.pattern` (a drum pattern:
     { kick, snare, hat, hatOpen? } arrays of step indices 0..15), stage
     checks read `.stages` ([{ id, layers }]), arrangement checks read
     `.sections` (['A','B',...]). --- */

  // Is there a kick on the very first step? Beat 1 is the groove's floor.
  beatHasKickOnOne: (beat) => {
    const pattern = (beat && beat.pattern) || {};
    if (!countBeatHits(pattern)) return { pass: false, message: EMPTY_BEAT_NUDGE };
    if ((pattern.kick || []).includes(0)) {
      return {
        pass: true,
        message: "Kick on the one — the floor of the whole groove. Feel it hold.",
      };
    }
    return {
      pass: false,
      message:
        "Nothing lands on the very first step, so the groove has no floor to " +
        "stand on. Give step 1 a kick — or keep the floating start if you like " +
        "the free-fall.",
    };
  },

  // Does the snare answer on 2 or 4 (steps 4 or 12)? That's the backbeat.
  beatHasBackbeat: (beat) => {
    const pattern = (beat && beat.pattern) || {};
    if (!countBeatHits(pattern)) return { pass: false, message: EMPTY_BEAT_NUDGE };
    const snare = pattern.snare || [];
    if (snare.includes(4) || snare.includes(12)) {
      return {
        pass: true,
        message: "There's the backbeat — 2 and 4 answering the kick like a handclap.",
      };
    }
    return {
      pass: false,
      message:
        "No snare on 2 or 4 yet — that's the backbeat, the part everyone claps " +
        "on. Drop one there, or keep the headless groove if that's the mood.",
    };
  },

  // Enough hits to feel like a beat at all.
  beatNotEmpty: (beat, minHits = 1) => {
    const pattern = (beat && beat.pattern) || {};
    const hits = countBeatHits(pattern);
    if (hits === 0) return { pass: false, message: EMPTY_BEAT_NUDGE };
    if (hits >= minHits) {
      return { pass: true, message: "Plenty of motion — this grid is alive." };
    }
    return {
      pass: false,
      message:
        `Only ${hits} hit${hits === 1 ? "" : "s"} so far — mostly air. A few ` +
        `more (aim for about ${minHits}) will give the groove a spine. Or keep ` +
        `it sparse if sparse is the point.`,
    };
  },

  // Do the stages actually change — some stage's layer mix differing from
  // another's? A build that never changes isn't building.
  stagesVary: (plan) => {
    const stages = (plan && plan.stages) || [];
    if (!stages.length) return { pass: false, message: EMPTY_STAGE_NUDGE };
    const mixes = new Set(stages.map(stageSignature));
    if (mixes.size > 1) {
      return {
        pass: true,
        message: "The stages breathe — something changes from one to the next.",
      };
    }
    return {
      pass: false,
      message:
        "Every stage has the same mix, so the song walks in place. Mute or add " +
        "a layer somewhere along the way and the energy starts to move. (Or " +
        "keep the trance. Trances are allowed.)",
    };
  },

  // Does the arrangement visit both weathers — an A section and a B section?
  arrangeHasContrast: (plan) => {
    const sections = (plan && plan.sections) || [];
    if (!sections.length) return { pass: false, message: EMPTY_ARRANGE_NUDGE };
    if (sections.includes("A") && sections.includes("B")) {
      return {
        pass: true,
        message: "A and B both on the map — your song gets both weathers.",
      };
    }
    return {
      pass: false,
      message:
        "It's all one weather so far. Slip in a B section (or an A, if it's " +
        "all shadow) and the song has somewhere to go — or keep the single " +
        "mood if the mood is the point.",
    };
  },
};

// Run a lesson's check specs against a phrase. Each spec is
// { id, check: <CHECKS key>, args: [...], message? } — an optional `message`
// overrides the check's own nudge when it doesn't pass (so lessons can carry
// bespoke copy). Unknown check names pass silently (forward compatibility
// with lesson data authored before a check ships).
export const runChecks = (checkSpecs = [], phrase) =>
  checkSpecs.map((spec) => {
    const fn = CHECKS[spec.check];
    if (!fn) return { id: spec.id, pass: true, message: "" };
    const { pass, message } = fn(phrase, ...(spec.args || []));
    return {
      id: spec.id,
      pass,
      message: !pass && spec.message ? spec.message : message,
    };
  });
